import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getMe } from "../features/authSlice";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import {
  FaArrowLeft,
  FaUpload,
  FaLeaf,
  FaBookOpen,
  FaFileAlt,
  FaImage,
  FaImages,
  FaTrash,
  FaTag,
  FaInstagram,
  FaChevronDown,
  FaNewspaper,
  FaPlus,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";

import { useStateContext } from "../contexts/ContextProvider";

// ── Quill toolbar config ─────────────────────────────────────────────────────
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link", "image"],
    [{ align: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "blockquote", "code-block",
  "link", "image", "align",
];

const KATEGORI_OPTIONS = ["Case Study", "News", "Article", "Post"];

const KATEGORI_COLORS = {
  "Case Study": "#1a4731",
  "News":       "#0f766e",
  "Article":    "#7c3aed",
  "Post":       "#be185d",
};

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ALBUM_PHOTOS = 50;

// ── Component ────────────────────────────────────────────────────────────────
const EditStories = () => {
  const [kategori, setKategori] = useState("");
  const [judul, setJudul] = useState("");
  const [tags, setTags] = useState("");
  const [deskripsi, setDeskripsi] = useState("");
  const [urlInstagram, setUrlInstagram] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDeletingCover, setIsDeletingCover] = useState(false);

  // ── Album (existing dari server + baru diupload) ──
  const [existingAlbum, setExistingAlbum] = useState([]); // [{ fileName, url }]
  const [newAlbumFiles, setNewAlbumFiles] = useState([]); // File[]
  const [newAlbumPreviews, setNewAlbumPreviews] = useState([]); // base64[]
  const [isDraggingAlbum, setIsDraggingAlbum] = useState(false);
  const [albumError, setAlbumError] = useState("");

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [kategoriOpen, setKategoriOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fileInputRef = useRef(null);
  const albumInputRef = useRef(null);
  const kategoriRef = useRef(null);
  const { currentColor, currentMode } = useStateContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { id } = useParams();

  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  const isPost = kategori === "Post";
  const isContent = ["Case Study", "News", "Article"].includes(kategori);
  const totalAlbumCount = existingAlbum.length + newAlbumFiles.length;

  useEffect(() => { dispatch(getMe()); }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) { getStoriesById(); } else { navigate("/"); }
  }, [navigate, user]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (kategoriRef.current && !kategoriRef.current.contains(e.target)) {
        setKategoriOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Fetch data ───────────────────────────────────────────────────────────
  const getStoriesById = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/stories/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data;
      setJudul(data.judul || "");
      setKategori(data.kategori || "");
      setTags(data.tags || "");
      setDeskripsi(data.deskripsi || "");
      setUrlInstagram(data.urlInstagram || "");
      setPreview(data.url || null);
      setFile(null);

      // album & urlAlbum tersimpan sebagai array (JSON) yang sejajar index-nya
      const albumNames = Array.isArray(data.album) ? data.album : [];
      const albumUrls = Array.isArray(data.urlAlbum) ? data.urlAlbum : [];
      const zipped = albumNames.map((fileName, i) => ({
        fileName,
        url: albumUrls[i] || null,
      }));
      setExistingAlbum(zipped);
    } catch (error) {
      console.error("Error fetching stories data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Kategori select ──────────────────────────────────────────────────────
  const handleKategoriSelect = (val) => {
    setKategori(val);
    setKategoriOpen(false);
    setErrors({});
    if (val === "Post") {
      setJudul(""); setTags(""); setDeskripsi("");
      setFile(null); setPreview(null);
      setExistingAlbum([]); setNewAlbumFiles([]); setNewAlbumPreviews([]); setAlbumError("");
    } else {
      setUrlInstagram("");
    }
  };

  // ── Validation ───────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!kategori) newErrors.kategori = "Kategori wajib dipilih";
    if (isPost) {
      if (!urlInstagram.trim()) newErrors.urlInstagram = "URL Instagram wajib diisi";
    } else if (isContent) {
      if (!judul.trim()) newErrors.judul = "Judul wajib diisi";
      if (!deskripsi || deskripsi === "<p><br></p>") newErrors.deskripsi = "Deskripsi wajib diisi";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Cover file handlers ──────────────────────────────────────────────────
  const processFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    // Hanya izinkan drop kalau belum ada cover aktif (harus hapus dulu)
    if (preview) return;
    processFile(e.dataTransfer.files[0]);
  };

  // Klik dropzone hanya buka file picker kalau BELUM ada cover aktif.
  // Kalau masih ada preview, user wajib hapus (FaTrash) dulu.
  const handleUploadClick = (e) => {
    e?.stopPropagation();
    if (preview) return;
    fileInputRef.current?.click();
  };

  // Hapus cover LANGSUNG dari server (tanpa perlu submit form dulu)
  const handleRemoveImage = async (e) => {
    e?.stopPropagation();
    if (isDeletingCover) return;

    // Kalau yang ada baru sebatas preview lokal (belum pernah tersimpan di server,
    // misal user baru saja pilih file baru tapi belum submit), cukup bersihkan state lokal.
    if (file) {
      setFile(null);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Kalau tidak ada preview sama sekali, tidak ada yang perlu dihapus
    if (!preview) return;

    try {
      setIsDeletingCover(true);
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.delete(`${apiUrl}/stories/${id}/image`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFile(null);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error deleting cover image:", error);
      alert(error.response?.data?.msg || "Gagal menghapus gambar cover");
    } finally {
      setIsDeletingCover(false);
    }
  };

  // ── Album file handlers ──────────────────────────────────────────────────
  const processNewAlbumFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    setAlbumError("");

    const room = MAX_ALBUM_PHOTOS - totalAlbumCount;
    if (room <= 0) {
      setAlbumError(`Maksimal ${MAX_ALBUM_PHOTOS} foto di dalam album.`);
      return;
    }

    const accepted = [];
    let rejectedReason = "";

    incoming.slice(0, room).forEach((f) => {
      if (!ALLOWED_TYPES.includes(f.type)) {
        rejectedReason = "Beberapa file dilewati karena bukan format JPG/PNG.";
        return;
      }
      if (f.size > MAX_SIZE) {
        rejectedReason = "Beberapa file dilewati karena ukuran lebih dari 5MB.";
        return;
      }
      accepted.push(f);
    });

    if (incoming.length > room) {
      rejectedReason = `Hanya ${room} foto ditambahkan, sisanya melebihi batas ${MAX_ALBUM_PHOTOS} foto.`;
    }
    if (rejectedReason) setAlbumError(rejectedReason);

    if (accepted.length === 0) return;

    setNewAlbumFiles((prev) => [...prev, ...accepted]);

    accepted.forEach((f) => {
      const reader = new FileReader();
      reader.onloadend = () => setNewAlbumPreviews((prev) => [...prev, reader.result]);
      reader.readAsDataURL(f);
    });
  };

  const handleAlbumChange = (e) => {
    processNewAlbumFiles(e.target.files);
    if (albumInputRef.current) albumInputRef.current.value = "";
  };
  const handleAlbumDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAlbum(true); };
  const handleAlbumDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAlbum(false); };
  const handleAlbumDrop = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAlbum(false); processNewAlbumFiles(e.dataTransfer.files); };
  const handleAlbumUploadClick = () => albumInputRef.current?.click();

  // Hapus foto lama (yang sudah tersimpan di server) — cukup keluarkan dari daftar "keep"
  const handleRemoveExistingAlbumImage = (index) => {
    setExistingAlbum((prev) => prev.filter((_, i) => i !== index));
    setAlbumError("");
  };
  // Hapus foto baru (yang baru dipilih, belum diupload)
  const handleRemoveNewAlbumImage = (index) => {
    setNewAlbumFiles((prev) => prev.filter((_, i) => i !== index));
    setNewAlbumPreviews((prev) => prev.filter((_, i) => i !== index));
    setAlbumError("");
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const updateStories = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("kategori", kategori);

    if (isPost) {
      formData.append("urlInstagram", urlInstagram.trim());
      formData.append("judul", "");
      formData.append("tags", "");
      formData.append("deskripsi", "");
    } else {
      formData.append("judul", judul.trim());
      formData.append("tags", tags.trim());
      formData.append("deskripsi", deskripsi);
      formData.append("urlInstagram", "");
      if (file) formData.append("file", file);

      // Foto lama yang tetap dipertahankan (yang tidak dihapus user)
      formData.append("keepAlbum", JSON.stringify(existingAlbum.map((a) => a.fileName)));
      // Foto baru yang ditambahkan
      newAlbumFiles.forEach((albumFile) => formData.append("album", albumFile));
    }

    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.patch(`${apiUrl}/stories/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      navigate("/list-stories");
    } catch (error) {
      console.error("Error updating stories:", error);
      alert(error.response?.data?.msg || "Gagal memperbarui data stories");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = (hasError) => ({
    backgroundColor: inputBg,
    borderColor: hasError ? "#ef4444" : inputBorder,
    color: textPrimary,
  });

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
        <div className="max-w-5xl mx-auto space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-6 rounded-xl border animate-pulse" style={{ backgroundColor: cardBg, borderColor }}>
              <div className="h-4 rounded mb-4 w-1/4" style={{ backgroundColor: `${accentColor}20` }} />
              <div className="h-10 rounded w-full" style={{ backgroundColor: `${accentColor}10` }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background deco */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaLeaf className="absolute top-20 left-10 text-8xl" style={{ color: accentColor }} />
          <FaBookOpen className="absolute bottom-20 right-20 text-7xl" style={{ color: accentColor }} />
        </div>
      </div>

      <div className="relative z-10 mx-auto">
        {/* ── Header ── */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/list-stories")}
            className="flex items-center gap-2 mb-4 transition-all duration-300 hover:opacity-80"
            style={{ color: textSecondary }}
          >
            <FaArrowLeft className="text-sm" />
            <span className="text-sm font-medium">Kembali ke Daftar Stories</span>
          </button>

          <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: accentColor }}>
                  <FaBookOpen className="text-white text-xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                      Stories Management
                    </span>
                    <span className="text-xs" style={{ color: textSecondary }}>—</span>
                    <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                      Rumoh Pangan Aceh
                    </span>
                  </div>
                  <h1 className="text-2xl font-serif font-bold" style={{ color: textPrimary }}>
                    Edit Stories
                  </h1>
                  <p className="text-sm mt-1" style={{ color: textSecondary }}>
                    Perbarui data stories yang ditampilkan di website
                  </p>
                </div>
              </div>
              {/* Kategori badge current */}
              {kategori && (
                <div
                  className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider"
                  style={{ backgroundColor: `${KATEGORI_COLORS[kategori]}20`, color: KATEGORI_COLORS[kategori] }}
                >
                  {kategori === "Post" && <FaInstagram className="text-sm" />}
                  {kategori}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Form ── */}
        <form onSubmit={updateStories}>
          <div className="space-y-6">

            {/* ── Kategori selector ── */}
            <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
              <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: textPrimary }}>
                <FaNewspaper className="text-sm" style={{ color: accentColor }} />
                Kategori
              </h3>

              <div ref={kategoriRef} className="relative max-w-sm">
                <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                  Kategori <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setKategoriOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-300 outline-none text-sm"
                  style={{
                    backgroundColor: inputBg,
                    borderColor: errors.kategori ? "#ef4444" : kategoriOpen ? accentColor : inputBorder,
                    color: kategori ? textPrimary : textSecondary,
                  }}
                >
                  <div className="flex items-center gap-2">
                    {kategori && (
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: KATEGORI_COLORS[kategori] || accentColor }} />
                    )}
                    <span>{kategori || "Pilih kategori..."}</span>
                  </div>
                  <FaChevronDown
                    className="text-xs transition-transform duration-200"
                    style={{ color: textSecondary, transform: kategoriOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>

                {/* Dropdown */}
                {kategoriOpen && (
                  <div
                    className="absolute z-50 w-full mt-1 rounded-xl border shadow-xl overflow-hidden"
                    style={{ backgroundColor: cardBg, borderColor }}
                  >
                    {KATEGORI_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => handleKategoriSelect(opt)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-all duration-150"
                        style={{
                          backgroundColor: kategori === opt ? `${accentColor}15` : "transparent",
                          color: kategori === opt ? accentColor : textPrimary,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${accentColor}10`)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = kategori === opt ? `${accentColor}15` : "transparent")}
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: KATEGORI_COLORS[opt] || accentColor }} />
                        <span className="font-medium">{opt}</span>
                        {opt === "Post" && (
                          <span className="ml-auto flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "#be185d20", color: "#be185d" }}>
                            <FaInstagram className="text-[9px]" /> Instagram
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {errors.kategori && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{errors.kategori}</p>}
              </div>

              {kategori && (
                <p className="mt-3 text-xs" style={{ color: textSecondary }}>
                  {isPost
                    ? "Kategori Post hanya memerlukan URL embed Instagram."
                    : `Kategori ${kategori} memerlukan gambar, judul, tags, dan deskripsi artikel.`}
                </p>
              )}
            </div>

            {/* ── Conditional: Post ── */}
            {isPost && (
              <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
                <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: textPrimary }}>
                  <FaInstagram className="text-sm" style={{ color: "#be185d" }} />
                  Informasi Post Instagram
                </h3>

                <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                  URL Instagram <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FaInstagram className="absolute left-4 top-1/2 -translate-y-1/2 text-sm" style={{ color: "#be185d" }} />
                  <input
                    type="url"
                    value={urlInstagram}
                    onChange={(e) => setUrlInstagram(e.target.value)}
                    placeholder="https://www.instagram.com/p/..."
                    className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                    style={inputStyle(errors.urlInstagram)}
                    onFocus={(e) => (e.target.style.borderColor = "#be185d")}
                    onBlur={(e) => (e.target.style.borderColor = errors.urlInstagram ? "#ef4444" : inputBorder)}
                  />
                </div>
                {errors.urlInstagram && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{errors.urlInstagram}</p>}

                {/* Live preview embed */}
                {urlInstagram && (
                  <div className="mt-5">
                    <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: textSecondary }}>
                      Preview Embed
                    </p>
                    <div className="rounded-xl overflow-hidden border" style={{ borderColor, maxWidth: "360px" }}>
                      <iframe
                        src={`${urlInstagram.split("?")[0].replace(/\/$/, "")}/embed/`}
                        className="w-full border-0"
                        style={{ minHeight: "440px", display: "block" }}
                        scrolling="no"
                        allowTransparency="true"
                        allow="encrypted-media"
                        title="Instagram preview"
                        loading="lazy"
                      />
                    </div>
                  </div>
                )}

                <p className="text-xs mt-3" style={{ color: textSecondary }}>
                  Masukkan URL postingan Instagram. Contoh: https://www.instagram.com/p/ABC123/
                </p>
              </div>
            )}

            {/* ── Conditional: Case Study / News / Article ── */}
            {isContent && (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Left – Image Upload (Cover + Album) */}
                <div className="lg:col-span-1">
                  <div className="p-6 rounded-xl border sticky top-6" style={{ backgroundColor: cardBg, borderColor }}>
                    <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: textPrimary }}>
                      <FaImage className="text-sm" style={{ color: accentColor }} />
                      Gambar Cover
                    </h3>
                    <div
                      className="relative rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden"
                      style={{
                        borderColor: isDragging ? accentColor : inputBorder,
                        backgroundColor: isDragging ? `${accentColor}15` : inputBg,
                        cursor: preview ? "default" : "pointer",
                      }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={handleUploadClick}
                    >
                      {preview ? (
                        <div className="relative">
                          <img
                            src={preview}
                            alt="Preview"
                            className="w-full h-56 object-cover"
                            style={{ opacity: isDeletingCover ? 0.4 : 1, transition: "opacity 0.2s" }}
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <button
                              type="button"
                              onClick={handleRemoveImage}
                              disabled={isDeletingCover}
                              className="p-3 rounded-lg bg-red-500/80 hover:bg-red-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {isDeletingCover ? (
                                <FaSpinner className="text-white text-sm animate-spin" />
                              ) : (
                                <FaTrash className="text-white text-sm" />
                              )}
                            </button>
                          </div>
                          {/* Current image label */}
                          {!file && preview && !isDeletingCover && (
                            <div className="absolute bottom-0 left-0 right-0 px-3 py-2 text-center" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                              <span className="text-white text-[10px] font-medium">Gambar saat ini</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${accentColor}20` }}>
                            <FaUpload className="text-2xl" style={{ color: accentColor }} />
                          </div>
                          <p className="text-sm font-medium text-center mb-1" style={{ color: textPrimary }}>
                            Drag & Drop gambar baru
                          </p>
                          <p className="text-xs text-center" style={{ color: textSecondary }}>
                            atau klik untuk memilih file
                          </p>
                          <p className="text-[10px] mt-3 px-3 py-1 rounded-full" style={{ color: textSecondary, backgroundColor: `${accentColor}15` }}>
                            Format: JPG, PNG (Maks. 5MB)
                          </p>
                        </div>
                      )}
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                    </div>
                    <p className="text-[11px] mt-3 text-center" style={{ color: textSecondary }}>
                      {preview
                        ? "Hapus gambar saat ini terlebih dahulu untuk bisa mengunggah gambar baru"
                        : "Klik atau drag & drop untuk mengunggah gambar cover"}
                    </p>

                    {/* ── Album Foto (existing + baru) ── */}
                    <div className="mt-6 pt-6 border-t" style={{ borderColor }}>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: textPrimary }}>
                          <FaImages className="text-xs" style={{ color: accentColor }} />
                          Album Foto
                        </h4>
                        <span
                          className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                        >
                          Opsional
                        </span>
                      </div>
                      <p className="text-xs mb-4" style={{ color: textSecondary }}>
                        Kelola foto galeri untuk artikel ini. Hapus foto lama atau tambahkan foto baru.
                      </p>

                      <div className="grid grid-cols-3 gap-2">
                        {/* Foto lama (sudah tersimpan di server) */}
                        {existingAlbum.map((item, idx) => (
                          <div
                            key={`existing-${idx}`}
                            className="relative group rounded-lg overflow-hidden aspect-square border"
                            style={{ borderColor: inputBorder }}
                          >
                            <img src={item.url} alt={`Album ${idx + 1}`} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => handleRemoveExistingAlbumImage(idx)}
                              className="absolute top-1 right-1 p-1.5 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            >
                              <FaTimes className="text-white text-[10px]" />
                            </button>
                          </div>
                        ))}

                        {/* Foto baru (belum diupload, hasil pilih user) */}
                        {newAlbumPreviews.map((src, idx) => (
                          <div
                            key={`new-${idx}`}
                            className="relative group rounded-lg overflow-hidden aspect-square border-2"
                            style={{ borderColor: accentColor }}
                          >
                            <img src={src} alt={`Album baru ${idx + 1}`} className="w-full h-full object-cover" />
                            <span
                              className="absolute bottom-1 left-1 text-[9px] font-semibold px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: accentColor, color: "#fff" }}
                            >
                              Baru
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveNewAlbumImage(idx)}
                              className="absolute top-1 right-1 p-1.5 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            >
                              <FaTimes className="text-white text-[10px]" />
                            </button>
                          </div>
                        ))}

                        {totalAlbumCount < MAX_ALBUM_PHOTOS && (
                          <div
                            onClick={handleAlbumUploadClick}
                            onDragOver={handleAlbumDragOver}
                            onDragLeave={handleAlbumDragLeave}
                            onDrop={handleAlbumDrop}
                            className="relative flex flex-col items-center justify-center aspect-square rounded-lg border-2 border-dashed cursor-pointer transition-all duration-300"
                            style={{
                              borderColor: isDraggingAlbum ? accentColor : inputBorder,
                              backgroundColor: isDraggingAlbum ? `${accentColor}15` : inputBg,
                            }}
                          >
                            <FaPlus className="text-sm mb-1" style={{ color: textSecondary }} />
                            <span className="text-[10px] font-medium text-center px-1" style={{ color: textSecondary }}>
                              Tambah
                            </span>
                          </div>
                        )}
                      </div>

                      {existingAlbum.length === 0 && newAlbumPreviews.length === 0 && totalAlbumCount === 0 && (
                        <p className="text-[11px] mt-3" style={{ color: textSecondary }}>
                          Belum ada foto album.
                        </p>
                      )}

                      <input
                        ref={albumInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAlbumChange}
                        className="hidden"
                      />

                      {albumError && (
                        <p className="text-xs mt-3" style={{ color: "#ef4444" }}>{albumError}</p>
                      )}

                      <div className="flex items-center justify-between mt-3">
                        <p className="text-[11px]" style={{ color: textSecondary }}>
                          {totalAlbumCount}/{MAX_ALBUM_PHOTOS} foto
                        </p>
                        <p className="text-[11px]" style={{ color: textSecondary }}>
                          Maks. 5MB / foto
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right – Form Fields */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Basic Info */}
                  <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
                    <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: textPrimary }}>
                      <FaFileAlt className="text-sm" style={{ color: accentColor }} />
                      Informasi Artikel
                    </h3>

                    {/* Judul */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                        Judul <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <FaFileAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-sm" style={{ color: textSecondary }} />
                        <input
                          type="text"
                          value={judul}
                          onChange={(e) => setJudul(e.target.value)}
                          placeholder="Masukkan judul artikel"
                          className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                          style={inputStyle(errors.judul)}
                          onFocus={(e) => (e.target.style.borderColor = accentColor)}
                          onBlur={(e) => (e.target.style.borderColor = errors.judul ? "#ef4444" : inputBorder)}
                        />
                      </div>
                      {errors.judul && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{errors.judul}</p>}
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                        Tags
                      </label>
                      <div className="relative">
                        <FaTag className="absolute left-4 top-1/2 -translate-y-1/2 text-sm" style={{ color: textSecondary }} />
                        <input
                          type="text"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          placeholder="Contoh: Ketahanan Pangan, Aceh, Pertanian"
                          className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                          style={inputStyle(false)}
                          onFocus={(e) => (e.target.style.borderColor = accentColor)}
                          onBlur={(e) => (e.target.style.borderColor = inputBorder)}
                        />
                      </div>
                      <p className="text-xs mt-1.5" style={{ color: textSecondary }}>Pisahkan setiap tag dengan koma (,)</p>
                    </div>
                  </div>

                  {/* Quill Editor */}
                  <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
                    <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: textPrimary }}>
                      <FaFileAlt className="text-sm" style={{ color: accentColor }} />
                      Deskripsi / Konten Artikel <span className="text-red-500">*</span>
                    </h3>
                    <div
                      className="rounded-lg overflow-hidden"
                      style={{ border: `1px solid ${errors.deskripsi ? "#ef4444" : inputBorder}` }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={deskripsi}
                        onChange={setDeskripsi}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Tulis konten artikel di sini..."
                      />
                    </div>
                    {errors.deskripsi && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{errors.deskripsi}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* ── Action Buttons ── */}
            {kategori && (
              <div
                className="p-6 rounded-xl border flex flex-col sm:flex-row gap-4 justify-end"
                style={{ backgroundColor: cardBg, borderColor }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/list-stories")}
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: isDark ? "#1a1a18" : "#f7f3ec", color: textPrimary, border: `1px solid ${borderColor}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isDark ? "#282C33" : "#e8e0d0")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isDark ? "#1a1a18" : "#f7f3ec")}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: accentColor }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#b5772a")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accentColor)}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <FaLeaf className="text-sm" />
                      Update Stories
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* ── Quill + Dark mode overrides ── */}
      <style>{`
        .ql-toolbar.ql-snow {
          border: none !important;
          border-bottom: 1px solid ${borderColor} !important;
          background-color: ${isDark ? "#1a1a18" : "#f7f3ec"} !important;
          padding: 10px 12px !important;
        }
        .ql-toolbar.ql-snow .ql-stroke { stroke: ${textSecondary} !important; }
        .ql-toolbar.ql-snow .ql-fill { fill: ${textSecondary} !important; }
        .ql-toolbar.ql-snow button:hover .ql-stroke,
        .ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: ${accentColor} !important; }
        .ql-toolbar.ql-snow button:hover .ql-fill,
        .ql-toolbar.ql-snow button.ql-active .ql-fill { fill: ${accentColor} !important; }
        .ql-toolbar.ql-snow .ql-picker-label { color: ${textSecondary} !important; }
        .ql-toolbar.ql-snow .ql-picker-label:hover { color: ${accentColor} !important; }
        .ql-toolbar.ql-snow .ql-picker-options {
          background: ${cardBg} !important;
          border-color: ${borderColor} !important;
          border-radius: 8px !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
        }
        .ql-toolbar.ql-snow .ql-picker-item { color: ${textPrimary} !important; }
        .ql-toolbar.ql-snow .ql-picker-item:hover { color: ${accentColor} !important; }
        .ql-container.ql-snow { border: none !important; font-size: 14px !important; }
        .ql-editor {
          min-height: 280px !important;
          background-color: ${inputBg} !important;
          color: ${textPrimary} !important;
          line-height: 1.85 !important;
          padding: 16px 18px !important;
        }
        .ql-editor.ql-blank::before { color: ${textSecondary} !important; font-style: normal !important; }
        .ql-editor p { margin-bottom: 1rem; color: ${isDark ? "#c8c4bc" : "#4a4a44"}; }
        .ql-editor h2 {
          font-family: Georgia, serif; font-size: 1.35rem; font-weight: 700;
          color: ${textPrimary}; margin: 2rem 0 0.75rem;
          padding-bottom: 0.4rem; border-bottom: 2px solid ${borderColor};
        }
        .ql-editor h3 { font-family: Georgia, serif; font-size: 1.1rem; font-weight: 700; color: ${textPrimary}; margin: 1.5rem 0 0.5rem; }
        .ql-editor strong { font-weight: 700; color: ${textPrimary}; }
        .ql-editor ul > li {
          position: relative; padding: 0.5rem 0.75rem 0.5rem 2.25rem;
          margin-bottom: 0.35rem;
          background: ${isDark ? "#1a1a1808" : "#f7f3ec"};
          border-left: 3px solid ${accentColor};
          color: ${isDark ? "#c8c4bc" : "#3a3a34"};
          font-size: 0.875rem; line-height: 1.7;
        }
        .ql-editor ul > li::before { content: '✳'; position: absolute; left: 0.6rem; top: 0.5rem; color: ${accentColor}; font-size: 0.7rem; }
        .ql-editor blockquote {
          border-left: 4px solid ${accentColor} !important;
          background: ${isDark ? "#1a1a18" : "#f7f3ec"} !important;
          padding: 1rem 1.25rem !important; margin: 1.5rem 0 !important;
          font-family: Georgia, serif !important; font-size: 1.05rem !important;
          font-style: italic !important; color: ${textPrimary} !important;
        }
        .ql-editor a { color: ${accentColor}; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default EditStories;